import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';

import {
    videoTemplateListService,
    createVideoTemplateService,
    updateVideoTemplateService,
    deleteVideoTemplateService,
} from '../../services/videoTemplateService';
import { Loader } from '../../components/util/Svg';
import Breadcrumb from '../../components/Breadcrumb';
import { getDisplayMessage } from '../../helper';

const EMPTY_FORM = {
    type: 'AI',
    name: '',
    prompt: '',
    video: null,
};

const VideoTemplates = () => {
    const [list, setList] = useState([]);
    const [loader, setLoader] = useState(true);
    const [typeFilter, setTypeFilter] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [createModal, setCreateModal] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [currentId, setCurrentId] = useState('');
    const [isDisabled, setIsDisabled] = useState(false);
    const [uploadProgress, setUploadProgress] = useState('');
    const [formError, setFormError] = useState('');
    const [existing, setExisting] = useState(null);

    const breadcrumb = [{ link: '', linkText: 'Video Templates' }];

    const handleClose = () => {
        setCreateModal(false);
        setForm(EMPTY_FORM);
        setErrors({});
        setCurrentId('');
        setExisting(null);
        setUploadProgress('');
        setFormError('');
        setIsDisabled(false);
    };

    const getList = (nextPage = page, nextType = typeFilter) => {
        setLoader(true);
        const params = `?page=${nextPage}&limit=10${nextType ? `&type=${encodeURIComponent(nextType)}` : ''}`;
        videoTemplateListService(params).then((res) => {
            setLoader(false);
            if (res.status) {
                setList((res.data && res.data.items) || []);
                setTotalPages((res.data && res.data.totalPages) || 1);
                setPage((res.data && res.data.page) || nextPage);
            } else {
                setList([]);
            }
        }).catch(() => {
            setLoader(false);
            setList([]);
        });
    };

    useEffect(() => {
        getList(1, typeFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeFilter]);

    const validate = () => {
        const nextErrors = {};
        if (!form.type) {
            nextErrors.type = 'Type is required';
        }
        if (form.type === 'AI' && !String(form.prompt || '').trim()) {
            nextErrors.prompt = 'Prompt is required for AI templates';
        }
        if (!currentId && !form.video) {
            nextErrors.video = 'Video file is required';
        }
        if (form.video && form.video.type && form.video.type.indexOf('video/') !== 0) {
            nextErrors.video = 'Upload a valid video file';
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError('');
        if (!validate()) {
            return;
        }

        setIsDisabled(true);
        setUploadProgress('Uploading assets...');
        const body = new FormData();
        body.append('type', form.type);
        body.append('name', form.name || '');
        body.append('prompt', form.prompt || '');
                        if (form.video) {
            body.append('video', form.video);
        }

        const request = currentId
            ? updateVideoTemplateService(currentId, body)
            : createVideoTemplateService(body);

        request
            .then((res) => {
                setIsDisabled(false);
                setUploadProgress('');
                if (res.status) {
                    handleClose();
                    getList(1, typeFilter);
                } else {
                    setFormError(getDisplayMessage(res.data, 'Failed to save template'));
                }
            })
            .catch((err) => {
                setIsDisabled(false);
                setUploadProgress('');
                setFormError(getDisplayMessage(err && err.response && err.response.data, 'Upload failed'));
            });
    };

    const handleEdit = (item) => {
        setCurrentId(item._id);
        setExisting(item);
        setForm({
            type: item.type || 'AI',
            name: item.name || '',
            prompt: item.prompt || '',
            video: null,
        });
        setCreateModal(true);
    };

    const handleDelete = (id) => {
        if (!window.confirm('Delete this video template?')) {
            return;
        }
        setList((prev) => prev.filter((item) => item._id !== id));
        deleteVideoTemplateService(id).then((res) => {
            if (!res.status) {
                getList(page, typeFilter);
            }
        }).catch(() => getList(page, typeFilter));
    };

    return (
        <div className="page_wrapper">
            <div className="search_bar d-flex align-items-center">
                <select
                    className="form-control"
                    style={{ maxWidth: 180 }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                >
                    <option value="">All Types</option>
                    <option value="AI">AI</option>
                    <option value="360">360</option>
                </select>
                <button
                    className="btn btn-primary btn-sm ml-2"
                    onClick={() => getList(1, typeFilter)}
                >
                    Refresh
                </button>
            </div>

            <Breadcrumb breadcrumb={breadcrumb} />

            <div className="justifydesign d-flex justify-content-between align-content-center page_header">
                <h2>Video Templates</h2>
                <span className="btn btn-primary" onClick={() => setCreateModal(true)}>
                    Create Template
                </span>
            </div>

            <div className="table-responsive cm_card px-3 py-0 mb-3 cm_table_min_height">
                {loader ? (
                    <div className="text-center py-5"><Loader /></div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Name</th>
                                <th>Prompt</th>
                                <th>Video</th>
                                <th>Created</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4">
                                        No video templates found
                                    </td>
                                </tr>
                            ) : (
                                list.map((item) => (
                                    <tr key={item._id}>
                                        <td>{item.type}</td>
                                        <td>{item.name || '-'}</td>
                                        <td style={{ maxWidth: 240 }}>
                                            <div className="text-truncate" title={item.prompt || ''}>
                                                {item.prompt || '-'}
                                            </div>
                                        </td>
                                        <td>
                                            {item.videoUrl ? (
                                                <a href={item.videoUrl} target="_blank" rel="noreferrer">
                                                    View
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td>{item.created ? moment(item.created).format('DD MMM YYYY') : '-'}</td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-outline-primary mr-2"
                                                onClick={() => handleEdit(item)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleDelete(item._id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 ? (
                <div className="d-flex justify-content-end mb-4">
                    <button
                        className="btn btn-sm btn-secondary mr-2"
                        disabled={page <= 1}
                        onClick={() => getList(page - 1, typeFilter)}
                    >
                        Prev
                    </button>
                    <span className="align-self-center mx-2">
                        Page {page} / {totalPages}
                    </span>
                    <button
                        className="btn btn-sm btn-secondary"
                        disabled={page >= totalPages}
                        onClick={() => getList(page + 1, typeFilter)}
                    >
                        Next
                    </button>
                </div>
            ) : null}

            <Modal show={createModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{currentId ? 'Edit Video Template' : 'Create Video Template'}</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {formError ? <div className="alert alert-danger">{formError}</div> : null}
                        {uploadProgress ? <div className="alert alert-info">{uploadProgress}</div> : null}

                        <div className="form-group">
                            <label>Type</label>
                            <select
                                className="form-control"
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                            >
                                <option value="AI">AI</option>
                                <option value="360">360</option>
                            </select>
                            {errors.type ? <small className="text-danger">{errors.type}</small> : null}
                        </div>

                        <div className="form-group">
                            <label>Name</label>
                            <input
                                type="text"
                                className="form-control"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Optional template name"
                            />
                        </div>

                        <div className="form-group">
                            <label>Prompt {form.type === 'AI' ? '*' : ''}</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                value={form.prompt}
                                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                                placeholder="Prompt used for AI video generation"
                            />
                            {errors.prompt ? <small className="text-danger">{errors.prompt}</small> : null}
                        </div>

                        <div className="form-group">
                            <label>Video Upload {currentId ? '(leave empty to keep existing)' : '*'}</label>
                            <input
                                type="file"
                                className="form-control"
                                accept="video/*"
                                onChange={(e) => setForm({ ...form, video: e.target.files[0] || null })}
                            />
                            {existing && existing.videoUrl ? (
                                <small className="d-block mt-1">
                                    Current: <a href={existing.videoUrl} target="_blank" rel="noreferrer">video</a>
                                </small>
                            ) : null}
                            {errors.video ? <small className="text-danger">{errors.video}</small> : null}
                        </div>

                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isDisabled}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isDisabled}>
                            {isDisabled ? 'Saving...' : currentId ? 'Update' : 'Create'}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>
        </div>
    );
};

export default VideoTemplates;
